import React, { useState } from 'react';
import { ChevronDown, Lightbulb } from 'lucide-react';

/**
 * Transparent inline dropdown icon to toggle reasoning steps.
 * Always shows reasoning icon; animates when status is "reasoning".
 * Uses a smooth pulsing animation and chevron rotation.
 *
 * Props:
 * @param {{ id: string; status?: string; function: { arguments: string } }[]} reasonCalls
 */
export default function ReasoningDropdown({ reasonCalls }) {
  const [open, setOpen] = useState(false);

  if (!Array.isArray(reasonCalls) || reasonCalls.length === 0) {
    return null;
  }

  const isReasoning = reasonCalls.some(call => call.status === 'reasoning');

  function extractPartialReason(raw) {
    const key = '"reasoning_text":"';
    const idx = raw.indexOf(key);
    if (idx >= 0) {
      // extract everything after key
      let fragment = raw.substring(idx + key.length);
      // remove only trailing literal characters "} or " without stripping whitespace/newlines
      fragment = fragment.replace(/(?:"\}|"|\})$/, '');
      // unescape common JSON escapes
      fragment = fragment.replace(/\\n/g, '\n').replace(/\\"/g, '"');
      return fragment;
    }
    return raw;
  }

  // Build list of reasoning items, showing parsed or partial reasoning_text
  const items = reasonCalls.map((call, idx) => {
    const raw = call.function.arguments;
    let text;
    try {
      const args = JSON.parse(raw);
      text = args.reasoning_text || args.action_description || '';
    } catch {
      // JSON incomplete: extract partial reasoning_text
      text = extractPartialReason(raw);
    }
    return { id: call.id, text: text || '...' };
  });

  return (
    <div className="flex items-start w-full gap-2 mt-2">
      {/* <div className="w-4" />  */}

      <div className="flex flex-col w-full">
        <button
          className="flex items-center py-1 cursor-pointer select-none group"
          onClick={() => setOpen(prev => !prev)}
        >
          {/* Always show reasoning icon; pulse when reasoning */}
          <Lightbulb
            className={`h-5 w-5 transition-all duration-300 
              ${isReasoning ? 'text-blue-400 animate-pulse' : 'text-zinc-400 group-hover:text-zinc-300'}`}
          />
          {/* Chevron: rotates on open, subtle hover color */}
          <ChevronDown
            className={`ml-2 h-5 w-5 transform transition-transform duration-200 
              ${open ? 'rotate-180 text-zinc-300' : 'text-zinc-400 group-hover:text-zinc-300'}`}
          />
        </button>

        {open && (
          <div className="pl-4 mt-1 space-y-3">
            {items.map((item, idx) => (
              <div key={item.id}>
                <p className="text-xs font-semibold text-zinc-500 uppercase mb-1">
                  Step {idx + 1}
                </p>
                <p className="text-xs text-zinc-200 whitespace-pre-wrap">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}